/**
 * AlarmsContainer — Alarm history timeline for a machine.
 * Fetches from GET /api/alarms/?machine_id=X
 * Used inside SubMachine.jsx as the "Alarms" tab.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import client from '../API/API';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtTimestamp(ts) {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch { return String(ts); }
}

const ALARM_META = {
  offline:       { color: '#C42B1C', bg: '#FDECEA', border: '#F5C0BC', icon: '🔴', label: 'Offline' },
  online:        { color: '#00875A', bg: '#E6F5F0', border: '#B3DFCF', icon: '✅', label: 'Back Online' },
  high_pressure: { color: '#B45309', bg: '#FEF3C7', border: '#FCD34D', icon: '⚠️', label: 'High Pressure' },
  low_pressure:  { color: '#B45309', bg: '#FEF3C7', border: '#FCD34D', icon: '⚠️', label: 'Low Pressure' },
  overload:      { color: '#B45309', bg: '#FEF3C7', border: '#FCD34D', icon: '⚠️', label: 'Overload' },
  critical:      { color: '#C42B1C', bg: '#FDECEA', border: '#F5C0BC', icon: '🚨', label: 'Critical' },
  warning:       { color: '#B45309', bg: '#FEF3C7', border: '#FCD34D', icon: '⚠️', label: 'Warning' },
  info:          { color: '#0897BE', bg: '#E6F5FA', border: '#B3DEF0', icon: 'ℹ️', label: 'Info' },
  other:         { color: '#6B7194', bg: '#F6F7F9', border: '#ECEDF1', icon: '🔔', label: 'Alert' },
};

function alarmMeta(type) {
  return ALARM_META[type] || ALARM_META.other;
}

// ── AlarmRow ──────────────────────────────────────────────────────────────────

function AlarmRow({ alarm, index }) {
  const meta = alarmMeta(alarm.alarm_type);
  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.2, delay: Math.min(index * 0.03, 0.3) }}
      style={{ display: 'flex', gap: 16, marginBottom: 12 }}
    >
      {/* Timeline column */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 32, flexShrink: 0 }}>
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: meta.bg, border: `2px solid ${meta.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, flexShrink: 0,
        }}>
          {meta.icon}
        </div>
        <div style={{ width: 2, flex: 1, background: '#ECEDF1', marginTop: 4 }} />
      </div>

      {/* Content */}
      <div style={{
        flex: 1, background: meta.bg,
        border: `1px solid ${meta.border}`,
        borderLeft: `3px solid ${meta.color}`,
        borderRadius: 8, padding: '10px 14px',
        marginBottom: 4,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 4 }}>
          <span style={{
            fontSize: 11, fontWeight: 700, color: meta.color,
            textTransform: 'uppercase', letterSpacing: '0.4px',
          }}>
            {meta.label}
          </span>
          <span style={{ fontSize: 11, color: '#B6BACA', fontFamily: 'DM Mono, monospace', flexShrink: 0 }}>
            {fmtTimestamp(alarm.timestamp)}
          </span>
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#0C0E16', marginBottom: 3 }}>
          {alarm.title}
        </div>
        <div style={{ fontSize: 12, color: '#6B7194', lineHeight: 1.5 }}>
          {alarm.message}
        </div>
      </div>
    </motion.div>
  );
}

// ── AlarmsContainer ───────────────────────────────────────────────────────────

export default function AlarmsContainer({ machineid }) {
  const [alarms,  setAlarms]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  const load = useCallback(async () => {
    if (!machineid) return;
    setLoading(true);
    setError('');
    try {
      const res = await client.get('/api/alarms/', { params: { machine_id: machineid, limit: 100 } });
      setAlarms(res.data.alarms || []);
    } catch (e) {
      if (e?.response?.status === 404) {
        setAlarms([]);
      } else {
        setError('Could not load alarm history.');
      }
    } finally {
      setLoading(false);
    }
  }, [machineid]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ fontFamily: 'var(--font-sans)', fontWeight: 500, fontSize: 13, color: 'var(--ink-900)' }}>
            Alarm History
          </div>
          <div style={{ fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--ink-500)', marginTop: 3 }}>
            {alarms.length > 0 ? `${alarms.length} events · most recent first` : 'Push-triggered events logged here'}
          </div>
        </div>
        <button
          onClick={load}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
            border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)',
            fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--ink-500)',
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-raised)'}
          onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-surface)'}
        >
          ↺ Refresh
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[1, 2, 3].map(i => (
            <div key={i} style={{ display: 'flex', gap: 16 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#ECEDF1', flexShrink: 0 }} />
              <div style={{ flex: 1, height: 72, borderRadius: 8, background: '#F6F7F9' }} />
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div style={{ padding: '40px 24px', textAlign: 'center', color: '#C42B1C', fontSize: 13 }}>
          {error}
          <br />
          <button onClick={load} style={{ marginTop: 12, padding: '7px 20px', borderRadius: 8, border: '1px solid #C42B1C', background: '#FDECEA', color: '#C42B1C', cursor: 'pointer', fontSize: 13 }}>
            Retry
          </button>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && alarms.length === 0 && (
        <div style={{ padding: '60px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 16 }}>✅</div>
          <div style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 15, color: 'var(--ink-700)', marginBottom: 8 }}>
            No Alarms
          </div>
          <div style={{ fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--ink-500)', lineHeight: 1.6 }}>
            No alarm events have been recorded for this machine.<br />
            Alarms are logged when push notifications are triggered.
          </div>
        </div>
      )}

      {/* Timeline */}
      {!loading && !error && alarms.length > 0 && (
        <div>
          {alarms.map((alarm, i) => (
            <AlarmRow key={alarm.id || i} alarm={alarm} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}
